import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
    portfolioSelector,
    proposalSelector,
    usePortfolioSelector,
} from 'domain/Portfolio';
import { calculateStrategyScore, getClientId } from 'utils';
import { useParams } from 'react-router-dom';
import { SELECETED_PRODUCT_TYPE } from 'pages/ClientOverview/constants';
import { useThreeBProductType } from 'hooks/isThreeBProduct';
import useLayoutController from 'components/Stepper/RouteBasedStepper/useLayoutController';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useStrategySelection } from '../../../../hooks/useStrategySelection';
import StrategySelection from '../../../../components/StrategySelection/StrategySelection';
import { Modal } from '../../../../../../../../ui-library';
import { useProductsList } from '../../../../../../../../hooks/useProducts';
import { useChangePlan } from '../../context';

const StrategySelectionPage = () => {
    const { portfolioId, dfsClientId } = useParams();
    const {
        i18n: { language },
    } = useTranslation();
    const clientId = useMemo(() => getClientId(dfsClientId), [dfsClientId]);
    const {
        productId,
        strategy: { setData: setStrategy, data: strategyData },
        riskProfile: {
            data: { recommendedStrategy: cachedStrategy },
        },
        isThreeBProduct: is3bProduct,
    } = useChangePlan() as any;
    const proposalManger = usePortfolioSelector(proposalSelector) as any;
    const { dataRaw: portfolio } = usePortfolioSelector(
        portfolioSelector,
    ) as any;
    const [
        initialPortfolioProductAttributes,
        setInitialPortfolioProductAttributes,
    ] = useState<Record<string, any>>({});
    const [productMissingError, setProductMissingError] = useState<
        string | null
    >(null);
    const [continueLoading, setContinueLoading] = useState(false);
    const { products: productsList } = useProductsList() as any;

    const {
        data: portfolioDetails,
    } = useGetCustomService({
        service: 'customInvestmentService',
        api: 'getLastApplicationByPortfolio',
        payload: {
            portfolioId,
            language,
        },
        enabled: !!portfolioId && is3bProduct,
        staleTime: 20 * 60 * 1000, // 20 minutes
        gcTime: 25 * 60 * 1000, // 25 minutes
    });

    const cachedInvestmentHorizon = useMemo(() => {
        if (portfolioDetails?.data?.investmentHorizon) {
            return portfolioDetails?.data?.investmentHorizon;
        }

        return null;
    }, [portfolioDetails?.data?.investmentHorizon]);

    const portfolioProposalId = useMemo(
        () => portfolio?.PortfolioProposalId,
        [portfolio],
    );

    const isIndividual = useMemo(
        () => !!portfolioProposalId,
        [portfolioProposalId],
    );

    const productSettings = useMemo(
        () => productsList.find((f: any) => f.id === productId)?.settings,
        [productsList, productId],
    );

    const { t } = useTranslation();
    const productExternalId = portfolio?.Product?.ExternalId;

    useEffect(() => {
        if (productExternalId) {
            sessionStorage.setItem(
                SELECETED_PRODUCT_TYPE,
                productExternalId || '',
            );
        }
    }, [productExternalId]);

    const {
        isZivZicAzp, isZiv, isZic, isAzp, isZivZifd, isZifd,
    } = useThreeBProductType({ product: { externalId: productExternalId } });

    const getIndividualData = useCallback(async () => {
        const portfolioProposal = await proposalManger.getProposal({
            clientId,
            proposalId: portfolioProposalId,
        });

        const individualPortfolio = portfolioProposal?.Positions?.map(
            (position: any) => ({
                securityId: position?.Security?.Id,
                allocation: position?.Allocation,
            }),
        );

        setStrategy((prev: any) => ({
            ...prev,
            individualPortfolio,
        }));
    }, [portfolioProposalId, clientId, proposalManger, setStrategy]);

    useEffect(() => {
        if (
            !isIndividual
            || strategyData?.individualPortfolio
            || continueLoading
        ) {
            return;
        }

        getIndividualData();
    }, [isIndividual, strategyData?.individualPortfolio]);

    const oldModelPortfolio = useMemo(
        () => productsList?.find(
            (item: any) => item.Id === parseInt(portfolio?.ModelPortfolioId, 10),
        ),
        [productsList, portfolio?.ModelPortfolioId],
    );

    const {
        investmentHorizon,
        selectedStrategyId,
        recommendedStrategy,
        selectedProductMarkId,
        setIndividualPortfolioData,
        onInvestmentHorizonChange,
        onProductMarkClick,
        strategies,
        strategy,
        setSelectedStrategyId,
        isIndividualPortfolioSelected,
        individualPortfolioData,
        isRebalance,
        setRebalance,
        selectedProduct,
        isNavigationDisabled,
        products,
        isModalListLoading,
        isAllocationsLoading,
        showChart,
        modelPortfolioDetails,
        setIndividualPortfolioValidationError,
        investmentHorizonError,
        setInvestmentHorizonError,
        productMarksOptions,
        allocations,
        factsheetUrl,
        checkTheRisk,
        productKey,
        preferenceStock,
        onPreferenceStockChange,
        preferenceExclusion,
        onPreferenceExclusionChange,
        preferenceInclusion,
        onPreferenceInclusionChange,
        exclusionSubstitute,
        onExclusionSubstituteChange,
        onSetupWithdrawalPlanChange,
        setupWithdrawalPlan,
        strategySelectedError,
        isZifdExclInterestIncome,
    } = useStrategySelection({
        productId,
        recommendedStrategyScore: calculateStrategyScore(
            cachedStrategy?.ScoreRangePercentage ?? 0,
        ),
        ...(!is3bProduct
            ? {
                additionalDisableCheck: (product: any) => oldModelPortfolio?.Id === product?.Id
                      || oldModelPortfolio?.Name === product?.Name,
                additionalCheckForProductList: (item: any) => item.Id !== oldModelPortfolio?.Id
                      && item?.Name !== oldModelPortfolio?.Name,
            }
            : {}),
        cachedInvestmentHorizon:
            strategyData?.investmentHorizon || cachedInvestmentHorizon,
        setPageErrors: () => undefined,
        productSettings,
        cachedModelPortfolioId:
            strategyData?.newModelPortfolio?.Id
            || (is3bProduct && portfolio?.ModelPortfolioId),
        ...(is3bProduct
            ? {
                cachedSetupWithdrawalPlan:
                      strategyData?.withdrawalOption
                      ?? initialPortfolioProductAttributes?.WithdrawalOption,
                cachedPreferenceStock:
                      strategyData?.preferenceStock
                      || initialPortfolioProductAttributes?.preferenceStock,
                cachedPreferenceExclusion:
                      strategyData?.preferenceExclusion
                      || initialPortfolioProductAttributes?.preferenceExclusion,
                cachedPreferenceInclusion:
                      strategyData?.preferenceInclusion
                      || initialPortfolioProductAttributes?.preferenceInclusion,
                cachedExclusionSubstitute:
                      strategyData?.exclusionSubstitute
                      || initialPortfolioProductAttributes?.exclusionSubstitute,
            }
            : {}),
        is3bProduct,
        isZiv,
        isZic,
        isAzp,
        isZifd,
    } as any);

    useEffect(() => {
        const rawAttributes = products?.find(
            (p: any) => portfolio?.ModelPortfolioId === p.Id,
        )?.Attributes;

        const parsed = JSON.parse(rawAttributes || '{}');

        setInitialPortfolioProductAttributes((prev: any) => {
            const prevString = JSON.stringify(prev);
            const nextString = JSON.stringify(parsed);

            return prevString === nextString ? prev : parsed;
        });
    }, [products, portfolio?.ModelPortfolioId]);

    const handleOnContinue = useCallback(() => {
        if (!selectedProductMarkId) {
            setContinueLoading(false);
            setProductMissingError(t('validation.mandatoryField'));

            return;
        }

        const strategyId = selectedStrategyId || recommendedStrategy?.id;
        const tempSetupWithdrawalPlan = isAzp || setupWithdrawalPlan;

        setStrategy({
            newModelPortfolio: selectedProduct,
            strategyId,
            strategyName: strategy?.Name,
            investmentHorizon: parseInt(investmentHorizon, 10),
            oldModelPortfolio,
            withdrawalOption: is3bProduct ? tempSetupWithdrawalPlan : undefined,
            preferenceStock,
            preferenceExclusion,
            exclusionSubstitute,
            preferenceInclusion,
            ...(isIndividualPortfolioSelected
                ? {
                    individualPortfolioPositions: individualPortfolioData,
                    individualPortfolio: individualPortfolioData.body.reduce(
                        (accu: any[], item: any) => {
                            if (!item?.allocation?.disabled) {
                                accu.push({
                                    securityId: item?.allocation?.id,
                                    allocation: item?.allocation?.value / 100,
                                });
                            }

                            return accu;
                        },
                        [],
                    ),
                    rebalancing: isRebalance,
                }
                : {
                    individualPortfolio: undefined,
                    individualPortfolioPositions: undefined,
                }),
        });
        setStepComplete(true);
    }, [
        isRebalance,
        selectedProduct,
        individualPortfolioData,
        investmentHorizon,
        selectedStrategyId,
        recommendedStrategy?.id,
        selectedProductMarkId,
        setupWithdrawalPlan,
        preferenceStock,
        preferenceExclusion,
        exclusionSubstitute,
        preferenceInclusion,
        is3bProduct,
        isAzp,
        isIndividualPortfolioSelected,
        t,
        setStrategy,
        strategy?.Name,
        oldModelPortfolio,
    ]);

    const onContinue = useCallback(() => {
        setContinueLoading(true);
        if (!investmentHorizon) {
            setInvestmentHorizonError(t('validation.mandatoryField'));
        }

        const isTheRiskChanged = checkTheRisk(handleOnContinue);

        if (isTheRiskChanged) {
            Modal.confirm(isTheRiskChanged);
            setContinueLoading(false);

            return;
        }

        handleOnContinue();
    }, [
        checkTheRisk,
        investmentHorizon,
        handleOnContinue,
        t,
        setInvestmentHorizonError,
    ]);

    useEffect(() => {
        if (selectedProductMarkId) {
            setProductMissingError(null);
        }
    }, [selectedProductMarkId]);

    const { setStepComplete } = useLayoutController({
        callbackOnContinue: onContinue,
        isContinueDisabled: isNavigationDisabled || continueLoading,
        isContinueLoading: continueLoading,
        isBackDisabled: false,
        reSubscriptionStates: [isIndividualPortfolioSelected, selectedProduct],
    });

    const state = {
        investmentHorizon,
        investmentHorizonError,
        selectedProduct,
        recommendedStrategy,
        selectedStrategyId,
        setSelectedStrategyId,
        products,
        isIndividualPortfolioSelected,
        isModalListLoading,
        isAllocationsLoading,
        individualPortfolioData,
        isRebalance,
        setRebalance,
        modelPortfolioDetails,
        setIndividualPortfolioData,
        setIndividualPortfolioValidationError,
        selectedProductMarkId,
        productMarksOptions,
        showChart,
        allocations,
        factsheetUrl,
        onInvestmentHorizonChange,
        onProductMarkClick,
        strategies,
        productMissingError,
        cachedSelectStrategy: strategyData?.strategyId
            ? strategyData
            : is3bProduct && { strategyId: portfolio?.RiskCategory?.Id },
        productKey,
        onSetupWithdrawalPlanChange,
        setupWithdrawalPlan,
        is3bProduct,
        preferenceStock,
        onPreferenceStockChange,
        preferenceExclusion,
        onPreferenceExclusionChange,
        preferenceInclusion,
        onPreferenceInclusionChange,
        exclusionSubstitute,
        onExclusionSubstituteChange,
        isZivZicAzp,
        isZiv,
        isAzp,
        isZivZifd,
        strategySelectedError,
        isZic,
        isZifdExclInterestIncome,
    };

    return <StrategySelection {...state} />;
};

export default StrategySelectionPage;
